// ============================================
// HYBRID CAMPAIGNS PAGE
// Matches Influencer.in design with BC integration
// Normal campaigns + BC enhancements
// Ported from HybridCampaigns.tsx (Next.js → React JSX)
// ============================================

import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import mixpanel from '../../utils/mixpanel';
import { getPlannerCampaigns, createPlannerCampaign, getPlannerObjectives, isValidBrandChannelHandle, isBrandChannelMatch, sanitizeBrandConnectCampaignTitle } from '../PlannerCampaign/api/plannerCampaignApi';
import InstagramRectIconButton from 'components/Planner/components/InstagramRectIconButton';
import { getCurrentUser } from '../../api_handlers/auth';

/** YCP / RFC 1034 label: first a–z, last a–z or digit, middle [a-z0-9-], max 63. */
const RFC1034_LABEL_RE = /^[a-z]([a-z0-9-]{0,61}[a-z0-9])?$/;

const POST_TYPES = {
    youtube: ['post', 'short'],
    instagram: ['post', 'reel', 'story'],
};

// -------------------- Helpers --------------------

function formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return String(num);
}

function formatCurrency(num) {
    if (num == null) return '-';
    if (num === 0) return 'Rs. 0';
    return `Rs. ${formatNumber(num)}`;
}

// -------------------- Campaign Card --------------------

function CampaignCard({ campaign, onClick }) {
    const statusColors = {
        draft: { bg: '#f3f4f6', text: '#4b5563' },
        active: { bg: '#d1fae5', text: '#065f46' },
        completed: { bg: '#dbeafe', text: '#1d4ed8' },
        archived: { bg: '#e5e7eb', text: '#4b5563' },
    };
    const sc = statusColors[campaign.status] || statusColors.draft;
    const yt = campaign.platforms?.youtube;
    const ig = campaign.platforms?.instagram;
    const bcData = campaign.bcData || yt?.bcData || null;

    return (
        <div
            onClick={onClick}
            style={{
                background: '#fff',
                borderRadius: 16,
                border: `1px solid ${campaign.bcEnabled ? '#fecaca' : '#f3f4f6'}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}
        >
            {/* Header */}
            <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{
                    width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                    background: campaign.bcEnabled
                        ? 'linear-gradient(135deg,#ef4444,#dc2626)'
                        : 'linear-gradient(135deg,#fbbf24,#f59e0b)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                }}>
                    {campaign.bcEnabled ? '🎬' : '📢'}
                </div>
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8, marginBottom: 4 }}>
                        <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {campaign.name}
                        </h3>
                        {campaign.bcEnabled && (
                            <span style={{ padding: '2px 6px', background: '#dc2626', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 4, flexShrink: 0 }}>
                                YCP
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                        Objective: <span style={{ color: '#374151' }}>{campaign.objective || 'Not specified'}</span>
                    </div>
                </div>
                <span style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    background: sc.bg, color: sc.text, flexShrink: 0, textTransform: 'capitalize',
                }}>
                    {campaign.status}
                </span>
            </div>

            {/* BC Campaign Code — show whenever a code exists, regardless of bcEnabled flag */}
            {campaign.bcCampaignCode && (
                <div style={{ padding: '0 20px 16px', display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '8px 16px', background: '#fef2f2', border: '2px solid #fecaca', borderRadius: 8 }}>
                        <div>
                            <div style={{ fontSize: 10, color: '#dc2626', textTransform: 'uppercase', fontWeight: 500, marginBottom: 2 }}>Campaign Code</div>
                            <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 20, color: '#dc2626', letterSpacing: 2 }}>
                                {campaign.bcCampaignCode}
                            </div>
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(campaign.bcCampaignCode || '');
                            }}
                            style={{ padding: 6, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}
                            title="Copy code"
                        >
                            📋
                        </button>
                    </div>
                </div>
            )}

            {/* Platform Stats — always rendered; shows '-' when API data is absent */}
            <div style={{ borderTop: '1px solid #f3f4f6' }}>
                {/* YouTube row */}
                <div style={{ padding: '16px 20px', borderBottom: ig ? '1px solid #f9fafb' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 6, background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ color: '#dc2626', fontSize: 13, fontWeight: 700 }}>▶</span>
                        </div>
                        <StatCell label="Overall Budget" value={formatCurrency(yt?.budget)} color="#d97706" />
                        <StatCell label="Videos" value={yt?.expectedVideos ?? '-'} />
                        <StatCell label="Duration" value={yt?.duration != null ? `${yt.duration}d` : '-'} />
                        <StatCell label="Start Date" value={campaign.startDate || '-'} small />
                        <StatCell label="End Date" value={campaign.endDate || '-'} small />

                        {campaign.bcEnabled && bcData && (
                            <>
                                <div style={{ width: 1, height: 40, background: '#fecaca', margin: '0 4px' }} />
                                <span style={{ padding: '2px 6px', background: '#fef2f2', color: '#dc2626', fontSize: 10, fontWeight: 600, borderRadius: 4 }}>YCP</span>
                                <StatCell label="Linked" value={bcData.videosLinked != null && bcData.videosTotal != null ? `${bcData.videosLinked}/${bcData.videosTotal}` : '-'} color="#dc2626" />
                                <StatCell label="Views" value={bcData.views != null ? formatNumber(bcData.views) : '-'} />
                                <StatCell label="Organic" value={bcData.organicPercent != null ? `${bcData.organicPercent}%` : '-'} color="#059669" highlight="#f0fdf4" />
                                <StatCell label="Eng. Rate" value={bcData.engagementRate != null ? `${bcData.engagementRate}%` : '-'} />
                            </>
                        )}
                    </div>
                </div>

                {/* Instagram row — only when platform data exists */}
                {ig && (
                    <div style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                            <div style={{ width: 32, height: 32, borderRadius: 6, background: 'linear-gradient(135deg,#a855f7,#ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ color: '#fff', fontSize: 13 }}>📷</span>
                            </div>
                            <StatCell label="Overall Budget" value={formatCurrency(ig.budget)} color="#d97706" />
                            <StatCell label="Posts" value={ig.expectedPosts ?? '-'} />
                            <StatCell label="Duration" value={ig.duration != null ? `${ig.duration}d` : '-'} />
                            <StatCell label="Start Date" value={campaign.startDate || '-'} small />
                            <StatCell label="End Date" value={campaign.endDate || '-'} small />
                        </div>
                    </div>
                )}
            </div>

            {/* No Brand Channel warning — shown for BC campaigns without a brand_channel_id */}
            {campaign.bcEnabled && !campaign.brandChannelId && (
                <div style={{ padding: '10px 20px', background: '#fefce8', borderTop: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>⚠️</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#92400e' }}>
                        No brand channel — video insights won't be available
                    </span>
                </div>
            )}

            {/* BC Progress Bar — always shown for BC campaigns */}
            {campaign.bcEnabled && (
                <div style={{ padding: '12px 20px', background: '#fef2f2', borderTop: '1px solid #fecaca' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#b91c1c', flexShrink: 0 }}>
                            Video Linking Progress:
                        </span>
                        <div style={{ flex: 1, height: 8, background: '#fecaca', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{
                                width: bcData?.videosTotal > 0
                                    ? `${(bcData.videosLinked / bcData.videosTotal) * 100}%`
                                    : '0%',
                                height: '100%',
                                background: 'linear-gradient(90deg,#ef4444,#dc2626)',
                                borderRadius: 4,
                                transition: 'width 0.3s',
                            }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', flexShrink: 0 }}>
                            {bcData?.videosTotal > 0
                                ? `${bcData.videosLinked}/${bcData.videosTotal} linked`
                                : `${bcData?.views != null ? bcData.views : 0} linked`}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCell({ label, value, color, small, highlight }) {
    const isEmpty = value === '-' || value == null || value === '';
    return (
        <div style={{
            textAlign: 'left',
            minWidth: small ? 64 : 56,
            padding: highlight ? '4px 8px' : 0,
            background: highlight || 'transparent',
            borderRadius: highlight ? 8 : 0,
        }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: isEmpty ? '#d1d5db' : (color || '#111827') }}>
                {isEmpty ? '-' : value}
            </div>
            <div style={{ fontSize: 10, color: highlight ? color : '#9ca3af', marginTop: 2 }}>{label}</div>
        </div>
    );
}

// -------------------- Create Campaign Modal --------------------

function CreateCampaignModal({ isOpen, onClose, onCreated }) {
    const [bcEnabled, setBcEnabled] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [objectives, setObjectives] = useState([]);
    const [activePlatform, setActivePlatform] = useState('yt');

    // Validation state for handle and channel ID
    const [handleValidation, setHandleValidation] = useState(null);
    const [channelIdValidation, setChannelIdValidation] = useState(null);
    const [validatingHandle, setValidatingHandle] = useState(false);
    const [validatingChannelId, setValidatingChannelId] = useState(false);
    const [matchValidation, setMatchValidation] = useState(null);
    const [validatingMatch, setValidatingMatch] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        getPlannerObjectives()
            .then(setObjectives)
            .catch(() => setObjectives([]));
        getCurrentUser()
            .then(response => {
                const data = response?.data;
                const brandName = data?.user_type === 'brand' ? (data?.brand?.name || '') : '';
                if (brandName) set('brand_name', brandName);
            })
            .catch(() => {});
    }, [isOpen]);

    const [form, setForm] = useState({
        name: '',
        brand_name: '',
        objective_id: '',
        start_date: '',
        end_date: '',
        yt_enabled: true,
        ig_enabled: false,
        yt_count: 1,
        yt_post_type: 'post',
        ig_count: 1,
        ig_post_type: 'post',
        brand_channel_handle: '',
        brand_channel_id: '',
        bc_brief: '',
        bc_environment: 'PROD',
        no_brand_channel: false,
    });

    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

    // Reset validation when the field value changes
    const handleHandleChange = (e) => {
        set('brand_channel_handle', e.target.value);
        setHandleValidation(null);
        setMatchValidation(null);
    };

    const handleChannelIdChange = (e) => {
        set('brand_channel_id', e.target.value);
        setChannelIdValidation(null);
        setMatchValidation(null);
    };

    const checkChannelMatch = async (handle, channelId) => {
        if (!handle || !channelId || form.no_brand_channel) return;
    
        setMatchValidation(null); 
        setValidatingMatch(true);
    
        try {
            const result = await isBrandChannelMatch(handle, channelId);
            setMatchValidation(result);
        } catch {
            setMatchValidation(false);
        } finally {
            setValidatingMatch(false);
        }
    };

    const handleHandleBlur = async () => {
        const handle = form.brand_channel_handle.trim();
        const channelId = form.brand_channel_id.trim();
    
        if (!handle || !channelId || form.no_brand_channel) return;
    
        setValidatingHandle(true);
        setValidatingChannelId(true);
        setValidatingMatch(true);
        try {
            const result = await isValidBrandChannelHandle(handle, channelId);
            setHandleValidation(result?.valid);
            setChannelIdValidation(result?.valid);
            // setMatchValidation(result?.match);
    
            // if (result && channelIdValidation !== false) {
            //     checkChannelMatch(handle, channelId);
            // }
        } catch {
            setHandleValidation(false);
            setChannelIdValidation(false);
            setMatchValidation(false);
        } finally {
            setValidatingHandle(false);
            setValidatingChannelId(false);
            setValidatingMatch(false);
        }
    };

    const handleChannelIdBlur = async () => {
        const handle = form.brand_channel_handle.trim();
        const channelId = form.brand_channel_id.trim();
    
        if (!handle || !channelId || form.no_brand_channel) return;
        
        setValidatingHandle(true);
        setValidatingChannelId(true);
        setValidatingMatch(true);
        try {
            const result = await isValidBrandChannelHandle(handle, channelId);
            setChannelIdValidation(result?.valid);
            setHandleValidation(result?.valid);
            // setMatchValidation(result?.match);
    
            // if (result && channelIdValidation !== false) {
            //     checkChannelMatch(handle, channelId);
            // }
        } catch {
            setHandleValidation(false);
            setChannelIdValidation(false);
            setMatchValidation(false);
        } finally {
            setValidatingHandle(false);
            setValidatingChannelId(false);
            setValidatingMatch(false);
        }
    };

    // const handleChannelIdBlur = async () => {
    //     const handle = form.brand_channel_handle.trim();
    //     const channelId = form.brand_channel_id.trim();
    
    //     // 🚫 Stop if either is missing
    //     if (!handle || !channelId || form.no_brand_channel) return;
    
    //     setValidatingChannelId(true);
    //     try {
    //         const result = await isValidBrandChannelId(channelId, handle);
    //         setChannelIdValidation(result);
    
    //         if (result && handleValidation !== false) {
    //             checkChannelMatch(handle, channelId);
    //         }
    //     } catch {
    //         setChannelIdValidation(false);
    //     } finally {
    //         setValidatingChannelId(false);
    //     }
    // };

    useEffect(() => {
        const handle = form.brand_channel_handle.trim();
        const channelId = form.brand_channel_id.trim();
    
        if (!handle || !channelId || form.no_brand_channel) return;
    
        if (validatingHandle || validatingChannelId) return;
    
        if (handleValidation === true && channelIdValidation === true) {
            checkChannelMatch(handle, channelId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        handleValidation,
        channelIdValidation,
        validatingHandle,
        validatingChannelId
    ]);

    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Campaign name is required.'); return; }
        if (!form.objective_id) { setError('Please select an objective.'); return; }
        if (!form.start_date || !form.end_date) { setError('Start and end dates are required.'); return; }
        if (!form.yt_enabled && !form.ig_enabled) {
            setError('Enable at least one platform.');
            return;
        }

        const nameTrim = form.name.trim();

        if (bcEnabled) {
            if (!/[a-z0-9]/i.test(nameTrim)) {
                setError('Campaign name must include at least one letter or number for YCP.');
                return;
            }
            const bcTitle = sanitizeBrandConnectCampaignTitle(nameTrim);
            if (!RFC1034_LABEL_RE.test(bcTitle)) {
                setError(
                    'Campaign name must form a valid YCP label: lowercase letters, numbers, and hyphens only; start with a letter, end with a letter or number; max 63 characters.',
                );
                return;
            }
            if (!form.yt_enabled) {
                setError('YCP requires YouTube. Enable YouTube under Platforms.');
                return;
            }
            if (!form.no_brand_channel) {
                const h = form.brand_channel_handle.trim();
                const cid = form.brand_channel_id.trim();
                if (!h || !cid) {
                    setError('Enter brand channel handle and channel ID, or check “I don’t have a Brand Channel”.');
                    return;
                }
                if (handleValidation === false || channelIdValidation === false) {
                    setError('Fix the brand channel handle or channel ID that failed validation.');
                    return;
                }
                if (handleValidation !== true || channelIdValidation !== true) {
                    setError('Leave the brand channel handle and channel ID fields so they can be validated, then try again.');
                    return;
                }
                let matched = matchValidation;
                if (matched !== true) {
                    try {
                        matched = await isBrandChannelMatch(h, cid);
                        setMatchValidation(matched);
                    } catch {
                        matched = false;
                    }
                    if (!matched) {
                        setError('Brand channel handle and channel ID do not match.');
                        return;
                    }
                }
            }
        }

        setError('');
        setSaving(true);
        const platforms = [];
        if (form.yt_enabled) platforms.push({ platform: 'youtube', post_type: form.yt_post_type, count: Number(form.yt_count) });
        if (form.ig_enabled) platforms.push({ platform: 'instagram', post_type: form.ig_post_type, count: Number(form.ig_count) });
        const payload = {
            name: nameTrim,
            brand_name: form.brand_name,
            objective_id: Number(form.objective_id),
            startDate: form.start_date,
            endDate: form.end_date,
            platforms,
            ...(bcEnabled ? {
                createAsBrandConnect: true,
                brandChannelHandle: form.brand_channel_handle.trim(),
                brandChannelId: form.brand_channel_id.trim(),
                bcBrief: form.bc_brief.trim(),
                bcEnvironment: process.env.REACT_APP_BC_ENV || "TEST",
            } : {}),
        };
        try {
            const created = await createPlannerCampaign(payload);
            mixpanel.track('campaign_created', {
                campaign_id: created?.id,
                campaign_name: form.name,
                platform: Object.keys(form.platforms || {}).join(','),
                bc_enabled: bcEnabled,
            });
            onCreated && onCreated(created);
            toast.success('Campaign created successfully');
            onClose();
        } catch (e) {
            let message = e?.detail || e?.message || 'Failed to create campaign. Please try again.';
            if (Array.isArray(message)) {
                message = message.map((x) => (x != null ? String(x) : '')).filter(Boolean).join(' ');
            }
            if (typeof message !== 'string') {
                message = String(message || 'Failed to create campaign. Please try again.');
            }
            if (/Invalid campaign or deal name|RFC-1034/i.test(message)) {
                message =
                    'Campaign name must be a YCP–safe label: lowercase letters, numbers, and hyphens only, up to 63 characters (no spaces).';
            } else if (message.indexOf('Failed to create campaign for request.') !== -1) {
                message = "Invalid channel";
            }
            setError(message);
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    // Reusable inline validation icon component
    const ValidationIcon = ({ validating, status }) => {
        if (validating) {
            return (
                <span style={validationIconStyle}>
                    {/* Spinner */}
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
                        <circle cx="7" cy="7" r="5.5" stroke="#d1d5db" strokeWidth="2" />
                        <path d="M7 1.5A5.5 5.5 0 0 1 12.5 7" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </span>
            );
        }
        if (status === true) {
            return (
                <span style={validationIconStyle} title="Valid">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="7" fill="#d1fae5" />
                        <path d="M3.5 7L6 9.5L10.5 4.5" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
            );
        }
        if (status === false) {
            return (
                <span style={validationIconStyle} title="Invalid">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="7" fill="#fee2e2" />
                        <path d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                </span>
            );
        }
        return null;
    };

    return (
        <>
            {/* Spinner keyframes injected once */}
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            <div
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
                onClick={onClose}
            >
                <div
                    style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' }}
                    onClick={e => e.stopPropagation()}
                >
                    <div style={{ padding: '22px 24px 16px', borderBottom: '1px solid #f3f4f6', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>Create Campaign</h2>
                    </div>

                    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

                        <Field label="Campaign Name *">
                            <input type="text" placeholder="e.g. Summer Campaign 2026" value={form.name} onChange={e => set('name', e.target.value)} style={fieldStyle} />
                            {bcEnabled && (
                                <p style={{ margin: '6px 0 0', fontSize: 11, color: '#6b7280', lineHeight: 1.45 }}>
                                    YCP will use a normalized name (lowercase, hyphens instead of spaces, max 63 characters). Avoid emoji-only or symbol-only names.
                                </p>
                            )}
                        </Field>

                        <Field label="Brand Name">
                            <input type="text" disabled value={form.brand_name} style={{ ...fieldStyle, background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }} />
                        </Field>

                        <Field label="Objective *">
                            <select value={form.objective_id} onChange={e => set('objective_id', e.target.value)} style={fieldStyle}>
                                <option value="">Select objective</option>
                                {objectives.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                        </Field>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <Field label="Start Date *"><input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} style={fieldStyle} /></Field>
                            <Field label="End Date *"><input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} style={fieldStyle} /></Field>
                        </div>

                        {/* Platforms */}
                        <Field label="Platforms">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                                    {[{ key: 'yt', label: 'YouTube' }, { key: 'ig', label: 'Instagram' }].map(({ key, label }) => (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setActivePlatform(key)}
                                            style={{
                                                flex: 1,
                                                padding: '9px 0',
                                                fontSize: 13,
                                                fontWeight: 500,
                                                border: 'none',
                                                borderRight: key === 'yt' ? '1px solid #e5e7eb' : 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: "5%",
                                                background: activePlatform === key ? '#fff' : '#f9fafb',
                                                color: activePlatform === key ? '#111827' : '#6b7280',
                                                transition: 'background 0.15s, color 0.15s',
                                            }}
                                        >
                                            <InstagramRectIconButton platform={key === 'yt' ? 'youtube' : 'instagram'} />
                                            {label}
                                        </button>
                                    ))}
                                </div>

                                {[{ key: 'yt', platform: 'youtube' }, { key: 'ig', platform: 'instagram' }].map(({ key, platform }) =>
                                    activePlatform === key ? (
                                        <>
                                            {(key === 'ig' || (key === 'yt' && !bcEnabled)) && (
                                                <div key={key} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                                        <Field label="Post Type">
                                                            <select
                                                                value={form[`${key}_post_type`]}
                                                                onChange={e => set(`${key}_post_type`, e.target.value)}
                                                                style={{ ...fieldStyle, padding: '8px 10px' }}
                                                            >
                                                                {POST_TYPES[platform].map(t => (
                                                                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                                                                ))}
                                                            </select>
                                                        </Field>
                                                        <Field label="Count">
                                                            <input
                                                                type="number"
                                                                min={1}
                                                                value={form[`${key}_count`]}
                                                                onChange={e => set(`${key}_count`, e.target.value)}
                                                                style={{ ...fieldStyle, padding: '8px 10px' }}
                                                            />
                                                        </Field>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : null
                                )}
                            </div>
                        </Field>

                        {/* BC Section */}
                        {bcEnabled && activePlatform === "yt" && (
                            <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12 }}>
                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                    <div>
                                        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                                            <span style={{ padding: '1px 6px', background: '#dc2626', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 4, marginRight: '1%' }}>YCP</span>
                                            Get verified organic/paid traffic split and detailed audience insights for YouTube videos.
                                        </p>
                                    </div>
                                </label>

                                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #fecaca', display: 'flex', flexDirection: 'column', gap: 12 }}>

                                    {/* Brand Channel Handle with validation */}
                                    <Field label="Brand Channel Handle">
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="text"
                                                placeholder="@YourBrand"
                                                value={form.brand_channel_handle}
                                                onChange={handleHandleChange}
                                                onBlur={handleHandleBlur}
                                                disabled={form.no_brand_channel}
                                                style={{
                                                    ...fieldStyle,
                                                    paddingRight: 36,
                                                    ...(form.no_brand_channel ? { background: '#f9fafb', color: '#9ca3af', cursor: 'not-allowed' } : {}),
                                                    ...(handleValidation === false ? { borderColor: '#fca5a5' } : {}),
                                                    ...(handleValidation === true ? { borderColor: '#6ee7b7' } : {}),
                                                }}
                                            />
                                            <ValidationIcon validating={validatingHandle} status={handleValidation} />
                                        </div>
                                    </Field>

                                    {/* Brand Channel ID with validation */}
                                    <Field label="Brand Channel ID">
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="text"
                                                placeholder="UCxxxxxxxxxxxxxxxxxx"
                                                value={form.brand_channel_id}
                                                onChange={handleChannelIdChange}
                                                onBlur={handleChannelIdBlur}
                                                // onChange={handleHandleChange}
                                                // onBlur={handleHandleBlur}
                                                disabled={form.no_brand_channel}
                                                style={{
                                                    ...fieldStyle,
                                                    paddingRight: 36,
                                                    ...(form.no_brand_channel ? { background: '#f9fafb', color: '#9ca3af', cursor: 'not-allowed' } : {}),
                                                    ...(channelIdValidation === false ? { borderColor: '#fca5a5' } : {}),
                                                    ...(channelIdValidation === true ? { borderColor: '#6ee7b7' } : {}),
                                                }}
                                            />
                                            <ValidationIcon validating={validatingChannelId} status={channelIdValidation} />
                                        </div>
                                    </Field>

                                    {/* Match validation message */}
                                    {form.brand_channel_handle && form.brand_channel_id && !form.no_brand_channel && (
                                        <div style={{ fontSize: 12, marginTop: 4, marginLeft: 2 }}>
                                            {validatingMatch && (
                                                <span style={{ color: '#6b7280' }}>Checking match...</span>
                                            )}
                                            {matchValidation === true && !validatingMatch && (
                                                <span style={{ color: '#16a34a' }}>
                                                    ✓ Handle and Channel ID match
                                                </span>
                                            )}
                                            {matchValidation === false && !validatingMatch && (
                                                <span style={{ color: '#dc2626' }}>
                                                    ✕ Handle and Channel ID do not match
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={form.no_brand_channel || false}
                                            onChange={e => {
                                                const checked = e.target.checked;
                                                setForm(f => ({
                                                    ...f,
                                                    no_brand_channel: checked,
                                                    ...(checked ? { brand_channel_handle: '', brand_channel_id: '' } : {}),
                                                }));
                                                if (checked) {
                                                    setHandleValidation(null);
                                                    setChannelIdValidation(null);
                                                }
                                            }}
                                            style={{ width: 15, height: 15 }}
                                        />
                                        <span style={{ fontSize: 12, color: '#6b7280' }}>I don't have a Brand Channel Handle or ID</span>
                                    </label>

                                    <Field label="Campaign Brief">
                                        <textarea
                                            rows={3}
                                            placeholder="Describe your campaign goals for creators..."
                                            value={form.bc_brief}
                                            onChange={e => set('bc_brief', e.target.value)}
                                            style={{ ...fieldStyle, resize: 'vertical' }}
                                        />
                                    </Field>

                                </div>
                            </div>
                        )}

                        {activePlatform === "yt" && (
                            <div>
                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={!bcEnabled} onChange={e => setBcEnabled(!(e.target.checked))} style={{ width: 20, height: 20, marginTop: 0 }} />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#111827', fontSize: 14 }}>
                                        Enable Organic Tracking
                                    </div>
                                </label>
                            </div>
                        )}
                    </div>

                    <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: 10, position: 'sticky', bottom: 0, background: '#fff' }}>
                        {error && <div style={{ padding: '10px 14px', background: '#fee2e2', borderRadius: 8, fontSize: 13, color: '#991b1b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{error}</div>}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <button onClick={onClose} style={secondaryBtnStyle} disabled={saving}>Cancel</button>
                            <button onClick={handleSubmit} style={{ ...primaryBtnStyle, opacity: saving ? 0.7 : 1 }} disabled={saving}>
                                {saving ? 'Creating...' : 'Create Campaign'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// Shared style for the absolute-positioned icon inside the input
const validationIconStyle = {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
};
function Field({ label, children }) {
    return (
        <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>{label}</label>
            {children}
        </div>
    );
}

const fieldStyle = {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
};

const primaryBtnStyle = {
    padding: '10px 20px',
    background: '#111827',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
};

const secondaryBtnStyle = {
    padding: '10px 20px',
    background: '#f9fafb',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
};

// -------------------- Filter Tabs --------------------

function FilterTabs({ activeFilter, onFilterChange }) {
    const tabs = [
        { id: 'all', label: 'All Campaigns', activeStyle: { background: '#111827', color: '#fff' } },
        { id: 'bc', label: 'Creator Partnerships', activeStyle: { background: '#dc2626', color: '#fff' }, badge: 'YCP' },
        { id: 'standard', label: 'Standard', activeStyle: { background: '#f59e0b', color: '#111827' } },
    ];

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
            {tabs.map(tab => {
                const isActive = activeFilter === tab.id;
                return (
                    <button
                        key={tab.id}
                        onClick={() => onFilterChange(tab.id)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '8px 16px',
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 500,
                            border: '1px solid',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            ...(isActive
                                ? { ...tab.activeStyle, borderColor: 'transparent' }
                                : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }),
                        }}
                    >
                        {tab.badge && (
                            <span style={{
                                padding: '1px 5px',
                                fontSize: 10,
                                fontWeight: 700,
                                borderRadius: 4,
                                ...(isActive
                                    ? { background: '#fff', color: '#dc2626' }
                                    : { background: '#dc2626', color: '#fff' }),
                            }}>
                                {tab.badge}
                            </span>
                        )}
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
}

// -------------------- Main Component --------------------

// kept for offline fallback only — removed from live render

export default function HybridCampaigns() {
    const history = useHistory();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [activeFilter, setActiveFilter] = useState('all');
    const [allCampaigns, setAllCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCampaigns = () => {
        setLoading(true);
        setError(null);
        getPlannerCampaigns()
            .then(data => setAllCampaigns(data.campaigns || []))
            .catch(() => setError('Failed to load campaigns. Please try again.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchCampaigns(); }, []);

    const campaigns = allCampaigns.filter(c => {
        if (activeFilter === 'bc') return c.bcEnabled;
        if (activeFilter === 'standard') return !c.bcEnabled;
        return true;
    });

    const bcCount = allCampaigns.filter(c => c.bcEnabled).length;
    const standardCount = allCampaigns.filter(c => !c.bcEnabled).length;

    return (
        <div style={{ padding: 24, background: '#f9fafb', minHeight: '100%' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#111827' }}>My Campaigns</h1>
                    <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
                        {loading ? 'Loading...' : `${allCampaigns.length} campaigns (${bcCount} YCP, ${standardCount} standard)`}
                    </p>
                </div>
                <button onClick={() => setShowCreateModal(true)} style={primaryBtnStyle}>
                    + Create Campaign
                </button>
            </div>

            {/* Filter Tabs */}
            <FilterTabs activeFilter={activeFilter} onFilterChange={setActiveFilter} />

            {/* Loading */}
            {loading && (
                <div style={{ textAlign: 'center', padding: 64 }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                    <div style={{ color: '#9ca3af', fontSize: 14 }}>Loading campaigns...</div>
                </div>
            )}

            {/* Error */}
            {!loading && error && (
                <div style={{ textAlign: 'center', padding: 48, background: '#fee2e2', borderRadius: 16 }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>⚠️</div>
                    <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: 12 }}>{error}</div>
                    <button onClick={fetchCampaigns} style={primaryBtnStyle}>Retry</button>
                </div>
            )}

            {/* Campaign Cards */}
            {!loading && !error && (
                <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {campaigns.map(campaign => (
                            <CampaignCard
                                key={campaign.id}
                                campaign={campaign}
                                onClick={() => history.push(`/brandconnect/campaign/${campaign.id}/dashboard`)}
                            />
                        ))}
                    </div>

                    {campaigns.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 16, border: '1px solid #f3f4f6' }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
                            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600, color: '#111827' }}>No campaigns found</h3>
                            <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
                                {activeFilter === 'bc'
                                    ? 'No Creator Partnerships campaigns yet. Create one to start tracking organic reach!'
                                    : activeFilter === 'standard'
                                        ? 'No standard campaigns found.'
                                        : 'Create your first campaign to get started.'}
                            </p>
                        </div>
                    )}
                </>
            )}

            {/* Create Modal */}
            <CreateCampaignModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={(newCampaign) => {
                    setAllCampaigns(prev => [newCampaign, ...prev]);
                }}
            />
        </div>
    );
}
